import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, CloseIcon } from 'tdesign-icons-react';
import { Button, Divider, MessagePlugin, RadioValue, SwitchValue } from 'tdesign-react';

import { AddDown, Crop, Up } from '@/components/icon';
import { MediaConfigType } from '@/const/richMedia';
import { UserResourceType } from '@/interface/library';
import { INewUploadIProps } from '@/interface/upload';
import Rename from '@/material/form-upload/components/rename';

import AudioEditor from '../../../audio/AudioEditor';
import { RichMedia } from '../../../components/rich-media';
import { uploadCos } from '../../../service';
import { IUploadDialogDialog } from '../../dialog';
import { VideoPlayer } from '../../player/video/video';
import { UploadContent } from '..';
import styles from '../index.module.less';
import UploadAIContent from './ai';
import LibraryContent from './library';

/**
 * 弹窗内容
 * 视频，音频
 */
const UploadDialogContent: React.FC<
IUploadDialogDialog &
INewUploadIProps & {
  close: () => void;
  open: () => void;
  visible: boolean;
  type?: UserResourceType;
  handleChange: (params: { url: string; name: string }) => void;
  handleDelete: () => void;
  handleRichMediaChange: (
    key: string,
    value: RadioValue | SwitchValue
  ) => void;
}> = (props) => {
  const {
    value,
    handleChange,
    disabled,
    visible,
    handleRichMediaChange,
    type,
    hasSetting = false,
  } = props;
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';

  const [showSetting, setShowSetting] = useState(true);
  const [isCrop, setIsCrop] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);

  const audioEditorRef = useRef<any>(null);
  const videoRef = useRef<any>(null);

  useEffect(() => {
    if (!visible) {
      videoRef?.current?.pause();
    }
    return () => {
      videoRef?.current?.pause();
    };
  }, [visible]);

  const handleCrop = () => {
    setCropLoading(true);
    try {
      audioEditorRef.current?.crop();
    } catch (error) {
      MessagePlugin.error('裁剪失败,请重试');
      closeCrop();
    }
  };

  const closeCrop = () => {
    setIsCrop(false);
    setCropLoading(false);
  };

  const onCrop = async (blob: Blob | null, name: string) => {
    if (!blob) {
      MessagePlugin.error('裁剪失败,请重试');
      closeCrop();
      return;
    }

    const file = new File([blob], name, { type: 'audio/wav' });
    const res = await uploadCos(file, {
      storyId,
      size: Math.ceil(file.size / 1024 / 1024),
    });
    if (res?.response?.url) {
      handleChange({ url: res.response.url, name });
    } else {
      MessagePlugin.error('上传失败,请重试');
    }

    closeCrop();
  };

  const onNameChange = (name: string) => {
    handleChange({ url: value?.url?.url || '', name });
  };

  const onVolumeChange = (volume: number) => {
    handleRichMediaChange('volume', volume);
  };

  const richMediaType = useMemo(() => {
    switch (type) {
      case UserResourceType.RESOURCE_TYPE_VIDEO:
        return MediaConfigType.VIDEO;
      case UserResourceType.RESOURCE_TYPE_AUDIO:
      default:
        return MediaConfigType.AUDIO;
    }
  }, [type]);

  const triggedSetting = () => {
    setShowSetting(!showSetting);
  };

  return (
    <div>
      <div className={styles.uploadWrap}>
        {!value?.url?.url ? (
          <div className={styles.line}>
            <div className={styles.item}>
              <UploadContent
                {...props}
                text={'上传BGM'}
                uploadAreaOnChange={handleChange}
              />
            </div>
            <div className={styles.item}>
              <UploadAIContent {...props} />
            </div>
            <div className={styles.item}>
              <LibraryContent {...props} />
            </div>
          </div>
        ) : (
          <>
            {type === UserResourceType.RESOURCE_TYPE_AUDIO ? (
              <div className={styles.audioWrap}>
                <div className={styles.title}>
                    <div style={{ width: 'fit-content', minWidth: 0 }}>
                      <Rename
                        value={value?.url?.name || '未命名'}
                        disabled={disabled}
                        onChange={onNameChange}
                      />
                    </div>
                  <div className={styles.crop}>
                    {isCrop
                      ? <div className={styles.btnGroup}>
                        <Button
                          theme='default'
                          disabled={disabled}
                          style={{ width: 32, height: 32 }}
                          onClick={() => setIsCrop(false)}
                        >
                          <CloseIcon size={16} />
                        </Button>
                        <Button
                          theme='primary'
                          disabled={disabled}
                          loading={cropLoading}
                          style={{ width: 32, height: 32 }}
                          onClick={handleCrop}
                        >
                          {!cropLoading && <CheckIcon size={16} />}
                        </Button>
                      </div>
                      : <Button
                        theme='default'
                        style={{ width: 80, height: 32 }}
                        disabled={disabled}
                        onClick={() => {
                          if (props?.disabled) return;
                          setIsCrop(true);
                        }}
                      >
                        <div className={styles.btnText}>
                          <Crop />裁剪
                        </div>
                      </Button>
                    }
                  </div>
                </div>
                <AudioEditor
                  ref={audioEditorRef}
                  url={value.url.url}
                  name={value?.url?.name}
                    volume={value?.volume}
                  height={64}
                  isEdit={true}
                  visible={visible}
                    disabled={disabled}
                  onCrop={onCrop}
                    onVolumeChange={onVolumeChange}
                />
              </div>
            ) : null}

            {type === UserResourceType.RESOURCE_TYPE_VIDEO ? (
                <div className={styles.videoWrap}>
                  <div style={{ width: 'fit-content', minWidth: 0, maxWidth: '100%' }}>
                    <Rename
                      value={value?.url?.name || '未命名'}
                      disabled={disabled}
                      onChange={onNameChange}
                    />
                  </div>
                  <div className={styles.videoPlay}>
                    <VideoPlayer
                      url={value?.url?.url}
                      wrapWidth={140}
                      wrapHeight={160}
                      playRef={videoRef}
                    />
                  </div>
              </div>
            ) : null}
          </>
        )}
        {hasSetting && <>
          <Divider align="center" >
            <div onClick={triggedSetting} className={styles.settingText}>
              <span>高级设置</span>
              {showSetting ? <Up /> : <AddDown />}
            </div>
          </Divider>
          {showSetting && <div className={styles.richMedia}>
            <RichMedia
              richMedia={{ type: richMediaType }}
              onChange={handleRichMediaChange}
              value={value}
              disabled={disabled}
            />
          </div>}
        </>}
      </div>
    </div>
  );
};

export default UploadDialogContent;
